import { Tooltip } from '@/shared/ui';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationTranscript } from '@/entities/conversations';
import { cn } from '@/shared/lib';

type Props = {
  className?: string;
  transcript: ConversationTranscript;
  onClick?: () => void;
  isConsultant?: boolean;
};

export const EmotionIndicator: FC<Props> = ({ className, transcript, onClick, isConsultant }) => {
  const { t } = useTranslation(['dialogs']);

  const emotionConfig = {
    positive: { label: t('emotion.positive'), color: 'bg-(--color-chat-emotion-positive)' },
    sad: { label: t('emotion.sad'), color: 'bg-(--color-chat-emotion-sad)' },
    neutral: { label: t('emotion.neutral'), color: 'bg-(--color-chat-emotion-neutral)' },
    angry: { label: t('emotion.angry'), color: 'bg-(--color-chat-emotion-angry)' },
  } as const;

  if (!(['angry', 'sad', 'neutral', 'positive'] as Array<keyof typeof emotionConfig>).some((key) => transcript[key])) {
    return null;
  }

  const hasNeutral = Boolean(transcript.neutral && transcript.neutral * 100 >= 1);
  const showBorder = isConsultant && hasNeutral;

  const segments = (['angry', 'sad', 'neutral', 'positive'] as Array<keyof typeof emotionConfig>).map((key) => {
    const value = transcript[key];
    const config = emotionConfig[key];

    if (!value || value * 100 < 1) {
      return null;
    }

    return (
      <Tooltip
        tooltip={
          <div className={'flex items-center gap-1.5 text-sm'}>
            <span className={cn('size-2 rounded-full shrink-0', config.color)} />
            <span>{`${config.label} ${Math.round(value * 100)}%`}</span>
          </div>
        }
        key={key}
        classNames={{ content: 'p-1' }}
      >
        <div
          key={key}
          className={cn(
            'relative h-full transition-all duration-200 cursor-pointer',
            config.color,
            'hover:brightness-110 hover:scale-y-125 hover:z-10',
          )}
          style={{ width: `${value * 100}%` }}
        />
      </Tooltip>
    );
  });

  return (
    <button
      type={'button'}
      tabIndex={-1}
      className={cn('relative flex h-1.5 rounded-full w-full', showBorder && 'p-px', className)}
      onClick={onClick}
    >
      <span className={'flex size-full rounded-full overflow-hidden'}>{segments}</span>
      {/*
       * Рамка — отдельный слой с opacity, а не border-(--chat-event)/40: переменная задана у облака,
       * и браузер без color-mix() (Chrome 109) нарисовал бы рамку сплошным цветом.
       */}
      {showBorder && (
        <span
          aria-hidden
          className={'pointer-events-none absolute inset-0 rounded-full border border-(--chat-event) opacity-40'}
        />
      )}
    </button>
  );
};
